import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import Logo from '../Loading/Logo';
import { RootState } from '../../store';
import { recordConsentDecision } from '../../store/ui';
import {
  getAnalyticsOptedOut,
  setEntryAcknowledged,
} from '../../utils/localStorage';
import useConsentChoice from './useConsentChoice';

/**
 * The trail-entry screen: shown once per player (until "Delete all saved
 * data" clears local storage) before they can use a trail. Three things, in
 * order of what a player actually needs to know first:
 *
 *  1. ATTRIBUTION — who is actually running this trail. Magical Trails is
 *     the platform; almost every trail is made and run by some OTHER
 *     organisation (a school, a PTA, a park) using it.
 *  2. SAFETY — a brief reminder that the route/locations are the trail
 *     creator's choice, and players are responsible for their own safety.
 *     This is the thing the single proceed button acknowledges — trail use
 *     is gated on reading this, not on an analytics choice.
 *  3. TRANSPARENCY — analytics here is anonymous, cookieless, aggregate-only
 *     (no persistent identifier, see src/index.tsx), so it does NOT block
 *     trail use and there is no Accept/Reject choice to make. There's just
 *     an opt-OUT toggle, defaulting to "on", that a player can flip either
 *     here or later from the menu's "Privacy choices" — both call the same
 *     `useConsentChoice` hook so they can never diverge.
 */
export default function GDPR() {
  const [open, setOpen] = useState(true);
  const [analyticsOptedOut, setAnalyticsOptedOutState] = useState(() =>
    getAnalyticsOptedOut()
  );
  const [saveError, setSaveError] = useState(false);
  const chooseConsent = useConsentChoice();
  const dispatch = useDispatch();
  const currentTrail = useSelector(
    (state: RootState) => state.trail.currentTrail
  );

  const attribution = currentTrail?.attribution ?? 'organisation';
  const organisationName = currentTrail?.organisationName;

  const handleToggleOptOut = (nextOptedOut: boolean) => {
    setAnalyticsOptedOutState(nextOptedOut);
    const persisted = chooseConsent(nextOptedOut);
    setSaveError(!persisted);
  };

  const proceed = () => {
    setEntryAcknowledged();
    dispatch(recordConsentDecision());
    setOpen(false);
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-1000" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="bg-mt-surface relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div className="mx-auto flex h-16 w-16 items-center justify-center">
                    <Logo baseSize={2} />
                  </div>
                  <div className="mt-3 sm:mt-5">
                    <DialogTitle
                      as="h3"
                      className="text-mt-ink text-center text-base leading-6 font-semibold"
                    >
                      Before you set off
                    </DialogTitle>

                    <div className="mt-4 space-y-3 text-left">
                      {/* 1. Attribution */}
                      <p className="text-mt-ink-soft text-sm">
                        {attribution === 'magicalTrails' ? (
                          <>This is a Magical Trails trail.</>
                        ) : organisationName ? (
                          <>
                            This trail is run by{' '}
                            <strong>{organisationName}</strong> using Magical
                            Trails.
                          </>
                        ) : (
                          <>
                            This trail is run by its creator using Magical
                            Trails.
                          </>
                        )}
                      </p>

                      {/* 2. Safety disclaimer — responsibility wording is
                          keyed to who actually chose the route, so a
                          first-party trail doesn't disclaim Magical Trails'
                          own choices. */}
                      <p className="text-mt-ink-muted text-sm">
                        {attribution === 'magicalTrails' ? (
                          <>
                            The route and locations on this trail were chosen by
                            Magical Trails.
                          </>
                        ) : organisationName ? (
                          <>
                            The route and locations on this trail were chosen by{' '}
                            <strong>{organisationName}</strong>, not by Magical
                            Trails.
                          </>
                        ) : (
                          <>
                            The route and locations on this trail were chosen by
                            its creator, not by Magical Trails.
                          </>
                        )}{' '}
                        You're responsible for your own safety while playing —
                        stay aware of your surroundings, follow the Highway Code
                        and any local traffic rules, and make sure an adult
                        supervises children at all times.
                      </p>

                      {/* 3. Analytics transparency + opt-out (not a blocking
                          consent gate — see file header) */}
                      <div className="bg-mt-surface-2 rounded-md p-3">
                        <p className="text-mt-ink-muted text-sm">
                          We collect anonymous, cookieless usage statistics
                          (like which trail points you've visited) to help us
                          improve the trails — not linked to your account or a
                          persistent identifier. It never affects whether you
                          can play. See our{' '}
                          <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noreferrer"
                            className="text-mt-ink underline"
                          >
                            Privacy Policy
                          </a>{' '}
                          for details, and change your mind anytime from the
                          menu's "Privacy choices".
                        </p>
                        <label
                          htmlFor="gdpr-analytics-opt-out"
                          className="mt-2 flex items-start gap-2"
                        >
                          <input
                            id="gdpr-analytics-opt-out"
                            type="checkbox"
                            checked={analyticsOptedOut}
                            onChange={(event) =>
                              handleToggleOptOut(event.target.checked)
                            }
                            className="border-mt-border text-mt-primary focus:ring-mt-primary mt-0.5 h-4 w-4 rounded"
                          />
                          <span className="text-mt-ink-soft text-sm">
                            Turn off anonymous usage stats
                          </span>
                        </label>
                        {saveError && (
                          <p className="text-mt-danger mt-1 text-xs">
                            We couldn't save this preference on this device —
                            it'll still apply for this visit, but you may need
                            to set it again next time.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 mb-4 sm:mt-6">
                  <button
                    type="button"
                    className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2"
                    onClick={proceed}
                  >
                    Let's go
                  </button>
                </div>
                <p className="text-mt-ink-faint text-center text-xs">
                  Magical Trails is a product of{' '}
                  <a
                    href="https://magiczebra.co.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Magic Zebra Ltd
                  </a>
                </p>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
